import type { ReactNode } from 'react';
import { IconInbox } from './Icons';
import logoFull from '../assets/logo-full.png';
import logoFullDark from '../assets/logo-full-dark.png';

/**
 * Presentational primitives shared by every screen.
 *
 * These exist so that a heading, a panel and an empty state look the same on all forty-one
 * pages without each one re-deciding its own spacing. Nothing here holds state or touches
 * the API - they are shapes, not behaviour.
 */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page__header">
      <div style={{ minWidth: 0 }}>
        <h1 className="page__title">{title}</h1>
        {subtitle && <p className="page__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="row-end" style={{ flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

/** A card with a header strip. Use when the content needs a name of its own. */
export function Panel({
  title,
  subtitle,
  actions,
  children,
  flush = false,
  footer,
  style,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Removes body padding — for a table or list that should meet the panel edge. */
  flush?: boolean;
  footer?: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <section className="panel" style={style}>
      {(title || actions) && (
        <header className="panel__head">
          <div className="grow">
            {title && <div className="panel__title">{title}</div>}
            {subtitle && <div className="panel__sub">{subtitle}</div>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}

      <div className={flush ? 'panel__body panel__body--flush' : 'panel__body'}>{children}</div>

      {footer && <footer className="panel__foot">{footer}</footer>}
    </section>
  );
}

/**
 * The whole-screen wait, shown while a stored session is checked on a cold load.
 *
 * A bare "Loading…" leaves the reader unsure whether the app is working or broken, and it is
 * the first thing anyone sees. This says whose app it is and shows motion, so the wait reads
 * as progress rather than as a stall.
 *
 * The bar is indeterminate on purpose - the session check has no measurable progress, and a
 * invented percentage that jumps to 100 is worse than an honest sweep. The global
 * prefers-reduced-motion rule in base.css stops the animation for anyone who asks it to; the
 * logo and the label carry the message on their own in that case.
 */
export function AppLoader({ label = 'Loading your workspace…' }: { label?: string }) {
  return (
    <div className="boot">
      <div className="boot__logo">
        <img className="auth__logo auth__logo--light" src={logoFull} alt="ZATians' Academy" />
        <img className="auth__logo auth__logo--dark" src={logoFullDark} alt="ZATians' Academy" />
      </div>

      <div className="boot__track" role="progressbar" aria-label={label}>
        <span className="boot__sweep" />
      </div>

      <p className="boot__label">{label}</p>
    </div>
  );
}

/** A labelled band that separates groups of content down a long page. */
export function SectionTitle({ children }: { children: ReactNode }) {
  return <div className="section__title">{children}</div>;
}

export function EmptyState({
  title,
  body,
  icon,
  action,
}: {
  title: ReactNode;
  body?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon ?? <IconInbox />}</div>
      <div className="empty__title">{title}</div>
      {body && <div className="empty__body">{body}</div>}
      {action && <div style={{ marginTop: 'var(--sp-2)' }}>{action}</div>}
    </div>
  );
}

export type StatTone = 'accent' | 'emerald' | 'amber' | 'rose' | 'sky' | 'neutral';

export function Stat({
  label,
  value,
  hint,
  tone = 'accent',
  icon,
  small = false,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTone;
  icon?: ReactNode;
  small?: boolean;
}) {
  return (
    <div className={`stat${tone === 'accent' ? '' : ` stat--${tone}`}`}>
      <div className="stat__label">
        {icon}
        {label}
      </div>
      <div className={small ? 'stat__value stat__value--sm' : 'stat__value'}>{value}</div>
      {hint && <div className="stat__hint">{hint}</div>}
    </div>
  );
}

export function Stats({ children }: { children: ReactNode }) {
  return <div className="stats">{children}</div>;
}

export type BadgeTone =
  | 'neutral' | 'success' | 'danger' | 'warning' | 'info' | 'accent';

export function Badge({
  children,
  tone = 'neutral',
  dot = false,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
}) {
  return (
    <span
      className={`badge${tone === 'neutral' ? '' : ` badge--${tone}`}${dot ? ' badge--dot' : ''}`}
    >
      {children}
    </span>
  );
}

/**
 * The fee status pill used across the money screens.
 *
 * One place decides how "settled", "part paid" and "owing" look, so the same fee cannot
 * appear green on one screen and orange on another.
 */
export function PaidBadge({ paid, partial }: { paid: boolean; partial?: boolean }) {
  if (paid) return <Badge tone="success" dot>Paid</Badge>;
  if (partial) return <Badge tone="warning" dot>Partial</Badge>;
  return <Badge tone="danger" dot>Due</Badge>;
}

export function Callout({
  children,
  tone = 'info',
  icon,
  title,
}: {
  children: ReactNode;
  tone?: 'info' | 'warning' | 'danger';
  icon?: ReactNode;
  title?: ReactNode;
}) {
  return (
    <div className={`callout${tone === 'info' ? '' : ` callout--${tone}`}`}>
      {icon}
      <div>
        {title && <div className="callout__title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

/** Key/value grid for the detail screens. */
export function DetailList({ children }: { children: ReactNode }) {
  return <dl className="dl">{children}</dl>;
}

export function Detail({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="dl__item">
      <dt className="dl__label">{label}</dt>
      <dd className="dl__value" style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

/** Loading placeholder used where a table skeleton would be the wrong shape. */
export function Skeleton({
  height = 12,
  width = '100%',
  radius,
}: {
  height?: number | string;
  width?: number | string;
  radius?: number | string;
}) {
  return <div className="skel" style={{ height, width, borderRadius: radius }} />;
}

export function SkeletonLines({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack-sm">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={`${[86, 64, 74, 52][i % 4]}%`} />
      ))}
    </div>
  );
}
